import React, { useCallback, useEffect, useState } from "react";
import { socket } from "./socket";
import log from "cslog";
import {
    canvasAtom,
    draftMessageListAtom,
    isSocketConnectedAtom,
    messageListAtom,
    socketStatusAtom,
    threadIdAtom,
    userInputsAtom,
} from "../atoms";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { restoreAccessToken } from "../../_helpers/auth";
import { nanoid } from "nanoid";
import { startCase } from "lodash";
import { queryClient } from "../../_helpers/query";

export const progressAtom = atom({});

function SocketManager({ me, onMessageReceived }) {
    // const [isConnected, setIsConnected] = useState(socket?.connected);
    const [isConnected, setIsConnected] = useAtom(isSocketConnectedAtom);
    const setSocketStatus = useSetAtom(socketStatusAtom);
    const setCanvas = useSetAtom(canvasAtom);
    const setUserInputs = useSetAtom(userInputsAtom);
    const [draft_message_list, setDraftMessageList] =
        useAtom(draftMessageListAtom);
    const thread_id = useAtomValue(threadIdAtom);
    const setMessageList = useSetAtom(messageListAtom);
    const [Progress, setProgress] = useAtom(progressAtom);

    useEffect(() => {
        if (socket?.connected) {
            setIsConnected(true);
            setSocketStatus("connected");
        } else {
            setIsConnected(false);
            setSocketStatus("disconnected");
            socket.auth = { token: restoreAccessToken() };
            socket.connect();
        }
    }, []);

    // log.d(me, "me in SockerManager");
    log.d(draft_message_list, "draft_message_list");

    const sendDraftMessages = () => {
        log.d(draft_message_list, "draft_message_list in sendDraftMessages");
        if (draft_message_list.length > 0) {
            draft_message_list.forEach((msg) => {
                socket.timeout(5000).emit(
                    "send_message",
                    {
                        thread_id: thread_id,
                        // message: msg
                        message: msg?.message || "",
                        attachements: msg?.attachements || [],
                        agent: msg?.agent,
                    },
                    () => {
                        // onSuccess
                    }
                );
            });
            setDraftMessageList([]);
            log.p("Draft list set to empty");
        }
    };

    const handleConnect = useCallback(() => {
        setIsConnected(true);
        setSocketStatus("connected");
        sendDraftMessages();
    }, []);

    const handleDisconnect = useCallback(() => {
        setIsConnected(false);
        setSocketStatus("disconnected");
    }, []);

    const handleNewMessage = useCallback((msg) => {
        if (msg?.type === "blank") {
            return;
        }
        log.d(msg, "RECEIBED New Message");
        onMessageReceived(msg);

        if (msg?.type == "action") {
            if (msg?.content?.action.includes("||")) {
                const [action_id, step_msg] = msg?.content?.action.split("||");
                setProgress((oldProgress) => {
                    const action_progress =
                        (oldProgress.task === action_id
                            ? oldProgress.steps
                            : []) || [];
                    return {
                        task: action_id,
                        steps: [...action_progress, step_msg],
                    };
                });
            } else {
                return msg;
            }
        }

        if (msg?.type === "code") {
            const content = msg.content;
            if (content?.language === "canvas") {
                setProgress({});
                const code = content?.code;
                const canvas_type = Object.keys(code)?.[0];

                onMessageReceived({
                    role: "ai",
                    type: "canvas",
                    content: {
                        type: canvas_type,
                        // data: code?.[canvas_type],
                        data: {
                            ...code?.[canvas_type],
                            history_id: code?.history_id,
                        },
                    },
                });

                log.p("Now invalidating qdb_token_plan");
                queryClient.invalidateQueries({
                    queryKey: ["items", "qdb_token_plan"],
                });

                switch (canvas_type) {
                    case "letter":
                        setCanvas({
                            show: true,
                            data: {
                                type: "letter",
                                template_id:
                                    "0a6747c4-d90e-4b83-81f2-af6d194bca1d",
                                data: code[canvas_type],
                            },
                        });
                        break;
                    case "document":
                        setCanvas({
                            show: true,
                            data: {
                                type: "document",
                                template_id:
                                    "0a6747c4-d90e-4b83-81f2-af6d194bca1d",
                                data: code[canvas_type],
                            },
                        });
                        break;
                    case "design":
                        setCanvas({
                            show: true,
                            // data: code[canvas_type]
                            data: {
                                type: "design",
                                // data: code[canvas_type],
                                data: {
                                    ...code[canvas_type],
                                    history_id: code?.history_id,
                                },
                            },
                        });
                        break;
                    case "linkedin_post":
                        setCanvas({
                            show: true,
                            data: {
                                type: "linkedin_post",
                                // data: code[canvas_type],
                                data: {
                                    ...code[canvas_type],
                                    history_id: code?.history_id,
                                },
                            },
                        });
                        break;
                    case "blog":
                        setCanvas({
                            show: true,
                            data: {
                                type: "blog",
                                // data: code[canvas_type],
                                data: {
                                    ...code[canvas_type],
                                    history_id: code?.history_id,
                                },
                            },
                        });
                        break;
                    case "image":
                        setCanvas({
                            show: true,
                            data: {
                                type: "image",
                                data: code[canvas_type],
                            },
                        });
                        break;
                    default:
                        break;
                }
            }
            if (content?.language === "need_input") {
                setUserInputs({
                    enable: true,
                    data: content?.code,
                });
            }
        }
    }, []);

    const handleConnectError = useCallback((err) => {
        setIsConnected(false);
        setSocketStatus("offline");

        setMessageList((old_list) => {
            const last_msg = old_list[old_list.length - 1];
            let new_messages = [];
            if (last_msg?.role === "ai" && last_msg?.type === "action") {
                new_messages = [
                    {
                        role: "ai",
                        type: "error",
                        content: {
                            error: "Server is offline",
                        },
                    },
                ];
            }
            return [...old_list, ...new_messages];
        });
    });

    useEffect(() => {
        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("new_message", handleNewMessage);
        socket.on("connect_error", handleConnectError);

        return () => {
            if (socket) {
                socket.removeAllListeners();
                socket.disconnect();
            }
        };
    }, []);

    return <></>;

    // return <div>
    //     <h4>Socket Manager</h4>
    //     {isConnected ? "Connected" : "Disconnected"}
    //     <button onClick={() => {
    //         send_message({ message: "Hello" })
    //     }}>Send</button>
    // </div>
}

export default SocketManager;

export const useSocket = () => {
    const [isLoading, setIsLoading] = useState(false);
    const setMessageList = useSetAtom(messageListAtom);
    const setDraftMessageList = useSetAtom(draftMessageListAtom);
    const setUserInputs = useSetAtom(userInputsAtom);
    const setCanvas = useSetAtom(canvasAtom);
    // const thread_id = useAtomValue(threadIdAtom)
    const [thread_id, setThreadId] = useAtom(threadIdAtom);

    // function sendImage(image) {
    //     const msg = {
    //         role: "user",
    //         type: "image",
    //         content: {
    //             url: image?.value,
    //         },
    //     };
    //     send(msg);
    // }

    function sendHumanReply(msg, agent) {
        log.d([agent, msg], "SEND: Human Reply");
        // let msg_text = "| Name | Value | \n";
        // msg_text += "| --- | --- | \n";
        let msg_text = "";
        Object.entries(msg).forEach(([key, value]) => {
            // msg_text += `<i>${startCase(key)}</i><br/>${value}<br/><br/>`;
            // msg_text += `| ${startCase(key)} | ${value} | \n`;
            msg_text += `* **${startCase(key)}**: ${value} \n`;
        });
        // msg_text = msg_text.slice(0, -10);
        const new_messages = [
            {
                role: "user",
                type: "text",
                content: {
                    text: msg_text,
                },
            },
        ];
        if (socket.connected) {
            new_messages.push({
                role: "ai",
                type: "action",
                content: {
                    action: "Thinking",
                },
            });
            setIsLoading(true);
            setUserInputs({
                enable: false,
                data: {},
            });
            socket.timeout(5000).emit(
                "send_human_reply",
                {
                    thread_id: thread_id,
                    message: JSON.stringify(msg),
                    // tool_call_id: tool_call_id,
                    agent: agent || "master",
                },
                () => {
                    setIsLoading(false);
                    setUserInputs({
                        enable: false,
                        data: {},
                    });
                }
            );
        }
        setMessageList((old_list) => {
            return [...old_list, ...new_messages];
        });
    }

    function sendMessage(msg, attachments = [], agent = "master") {
        const new_messages = [
            {
                role: "user",
                type: "text",
                content: {
                    text: msg,
                },
                attachments: attachments,
            },
        ];
        log.d(new_messages, "SEND: New Messages");
        if (socket.connected) {
            new_messages.push({
                role: "ai",
                type: "action",
                content: {
                    action: "Thinking",
                },
            });
            setIsLoading(true);

            socket.timeout(5000).emit(
                "send_message",
                {
                    thread_id: thread_id,
                    message: msg,
                    attachments: attachments,
                    agent: agent,
                },
                () => {
                    setIsLoading(false);
                    // onSuccess
                }
            );
        } else {
            new_messages.push({
                role: "ai",
                type: "action",
                content: {
                    action: "Connecting to server...",
                    // action: "Thinking"
                },
            });
            setDraftMessageList([
                {
                    message: msg,
                    agent: agent,
                    attachments: attachments,
                },
            ]);

            socket.auth = { token: restoreAccessToken() };
            socket.connect();
            log.p("Now connecting with socket");
        }

        setMessageList((old_list) => {
            return [...old_list, ...new_messages];
        });
    }

    // function reset() {
    //     setIsLoading(true);
    //     socket.timeout(5000).emit('send_command', { command: "/reset" }, () => {
    //         setIsLoading(false);
    //         setMessageList([]);
    //     })
    // }

    function reset() {
        setMessageList([]);
        setUserInputs({
            enable: false,
            data: {},
        });
        setCanvas({
            show: false,
            data: {},
        });
        setThreadId(nanoid(7));
    }

    // function reset() {
    //     setIsLoading(true);
    //     if (socket.connected) {
    //         socket.disconnect();
    //     }

    //     setTimeout(() => {
    //         socket.auth = { token: restoreAccessToken() }
    //         socket.connect();
    //         setIsLoading(false);
    //         setMessageList([]);
    //         setUserInputs({
    //             enable: false,
    //             data: {}
    //         })
    //         setCanvas({
    //             show: false,
    //             data: {}
    //         })
    //     }, 100);
    // }

    return { isLoading, sendMessage, reset, sendHumanReply };
};
